import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Gavel, Trophy, Package, Star, Filter, AlertCircle, Save, Bell, BellOff, Download, FileText, Palmtree, Award, Sparkles, Rocket, Volume2, VolumeX, ShieldCheck , Tag, MapPin } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, API } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import Countdown from "../components/Countdown";
import PaymentScoreBadge from "../components/PaymentScoreBadge";
import LoyaltyInfoDialog from "../components/LoyaltyInfoDialog";
import ReceivedRatings from "../components/ReceivedRatings";
import PendingRatingsBanner from "../components/PendingRatingsBanner";
import ConformityClauseGate from "../components/ConformityClauseGate";
import WhatsAppPrefs from "../components/WhatsAppPrefs";
import ChangePasswordCard from "../components/ChangePasswordCard";
import SupplierOffersPanel from "../components/SupplierOffersPanel";
import CommissionDuesBanner from "../components/CommissionDuesBanner";
import { useAuth } from "../context/AuthContext";
import { usePush } from "../hooks/usePush";

const STATUS_LABEL = {
  open: "In asta", awarded: "Aggiudicato", paid: "Pagato",
  delivered: "Consegnato", expired: "Scaduto", cancelled: "Annullato", reassigning: "In riassegnazione",
};
const STATUS_COLOR = {
  open: "bg-amber-100 text-amber-700 border-amber-200",
  awarded: "bg-blue-100 text-blue-700 border-blue-200",
  paid: "bg-emerald-100 text-emerald-700 border-emerald-200",
  delivered: "bg-slate-100 text-slate-700 border-slate-200",
  expired: "bg-red-100 text-red-700 border-red-200",
  reassigning: "bg-orange-100 text-orange-700 border-orange-200",
};

export default function SupplierDashboard() {
  const { user, refresh } = useAuth();
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState(new URLSearchParams(window.location.search).get("tab") || "open");
  const tabsRef = useRef(null);
  const selectTab = (t) => { setTab(t); setTimeout(() => tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50); };
  const [loading, setLoading] = useState(true);
  const [cats, setCats] = useState([]);
  const [upgrading, setUpgrading] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);
  const push = usePush();

  const isPremium = ["basic","pro","enterprise","premium"].includes(user?.supplier_plan);
  const curPlanLabel = ({basic:"Basic",pro:"Pro",enterprise:"Enterprise",premium:"Premium (legacy)"})[user?.supplier_plan] || "";
  const inOnboarding = user?.zero_fee_reason === "onboarding";
  const daysLeft = user?.onboarding_days_left || 0;

  const upgrade = async (plan = "basic") => {
    setUpgrading(true);
    try {
      const { data } = await api.post(`/supplier-premium/checkout?plan=${plan}`, { origin_url: window.location.origin });
      const target = (window.top && window.top !== window) ? window.top : window;
      try { target.location.href = data.checkout_url; }
      catch (_e) { window.location.href = data.checkout_url; }
    } catch (e) { toast.error(e.response?.data?.detail || e.message); setUpgrading(false); }
  };

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post("/premium/portal", { origin_url: window.location.origin });
      const target = (window.top && window.top !== window) ? window.top : window;
      try { target.location.href = data.portal_url; }
      catch (_e) { window.location.href = data.portal_url; }
    } catch (e) { toast.error(e.response?.data?.detail || e.message); setPortalBusy(false); }
  };

  // filters
  const [catFilter, setCatFilter] = useState("all");
  const [minRating, setMinRating] = useState(0);
  const [minPayScore, setMinPayScore] = useState(0);
  const [maxPrice, setMaxPrice] = useState("");
  const [expressOnly, setExpressOnly] = useState(false);
  // Sound preference (persisted per-device via localStorage)
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try { return localStorage.getItem("ga_express_sound") !== "off"; } catch (_) { return true; }
  });
  const toggleSound = () => {
    setSoundEnabled(prev => {
      const next = !prev;
      try { localStorage.setItem("ga_express_sound", next ? "on" : "off"); } catch (_) { /* storage unavailable — preference not persisted */ }
      toast(next ? "🔔 Suono express attivo" : "🔕 Suono express silenziato");
      return next;
    });
  };

  // profile modal
  const [profileOpen, setProfileOpen] = useState(false);
  const [pp, setPp] = useState(user?.paypal_email || "");
  const [iban, setIban] = useState(user?.iban || "");
  const [ibanHolder, setIbanHolder] = useState(user?.iban_holder || "");
  const [payoutPassword, setPayoutPassword] = useState("");
  const [radius, setRadius] = useState(user?.delivery_radius_km || 0);
  const [address, setAddress] = useState(user?.address || "");
  const [workDays, setWorkDays] = useState(user?.working_days || [0, 1, 2, 3, 4, 5]);
  const [workFrom, setWorkFrom] = useState(user?.working_hours_from || "");
  const [workTo, setWorkTo] = useState(user?.working_hours_to || "");
  const [closedDates, setClosedDates] = useState(user?.closed_dates || []);
  const [newClosed, setNewClosed] = useState("");
  const [vacMode, setVacMode] = useState(!!user?.vacation_mode);
  const [vacUntil, setVacUntil] = useState(user?.vacation_until ? user.vacation_until.slice(0, 10) : "");
  const [profSaving, setProfSaving] = useState(false);

  // report modal
  const now = new Date();
  const [repOpen, setRepOpen] = useState(false);
  const [repYear, setRepYear] = useState(now.getFullYear());
  const [repMonth, setRepMonth] = useState(now.getMonth() + 1);
  const [repBusy, setRepBusy] = useState(false);

  useEffect(() => {
    setPp(user?.paypal_email || "");
    setIban(user?.iban || "");
    setIbanHolder(user?.iban_holder || "");
    setRadius(user?.delivery_radius_km || 0);
    setVacMode(!!user?.vacation_mode);
    setVacUntil(user?.vacation_until ? user.vacation_until.slice(0, 10) : "");
  }, [user?.paypal_email, user?.iban, user?.iban_holder, user?.delivery_radius_km, user?.vacation_mode, user?.vacation_until]);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCats(data)).catch(() => {});
  }, []);

  useEffect(() => {
    let m = true;
    // Ding audio + tracker for NEW express order ids arrived between polls
    const seenExpressIds = new Set();
    let firstLoad = true;
  const playDing = () => {
      if (!soundEnabled) return;
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        const now = ctx.currentTime;
        // Two-tone quick ding: 880Hz then 1320Hz
        [{f: 880, s: now, d: 0.12}, {f: 1320, s: now + 0.13, d: 0.18}].forEach(({f, s, d}) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = "sine";
          osc.frequency.setValueAtTime(f, s);
          gain.gain.setValueAtTime(0.0001, s);
          gain.gain.exponentialRampToValueAtTime(0.25, s + 0.01);
          gain.gain.exponentialRampToValueAtTime(0.0001, s + d);
          osc.connect(gain).connect(ctx.destination);
          osc.start(s);
          osc.stop(s + d + 0.02);
        });
        setTimeout(() => ctx.close(), 800);
      } catch (_) { /* AudioContext blocked or unsupported — expected on iOS Lockdown / no-gesture pages */ }
    };
    const load = async () => {
      try {
        const { data } = await api.get("/orders");
        if (!m) return;
        // Detect new express orders arrived since last poll
        const currentExpressIds = data.filter(o => o.status === "open" && o.express_max_price).map(o => o.id);
        if (!firstLoad) {
          const fresh = currentExpressIds.filter(id => !seenExpressIds.has(id));
          if (fresh.length > 0) {
            playDing();
            toast.success(`⚡ ${fresh.length} nuovo ordine express disponibile`, { duration: 5000 });
          }
        }
        currentExpressIds.forEach(id => seenExpressIds.add(id));
        firstLoad = false;
        setOrders(data);
      } catch (e) { if (process.env.NODE_ENV === "development") console.warn("supplier dashboard load failed:", e); } finally { if (m) setLoading(false); }
    };
    load();
    const t = setInterval(load, 10000);
    return () => { m = false; clearInterval(t); };
  }, [soundEnabled]);

  const open = orders.filter(o => o.status === "open");
  const won = orders.filter(o => o.winner && o.winner.id === user?.id);
  const activeWon = [...orders.filter(o => o.proposal), ...won.filter(o => ["awarded", "paid"].includes(o.status))];
  const delivered = won.filter(o => o.status === "delivered");

  const filteredOpen = useMemo(() => {
    return open.filter(o => {
      if (expressOnly && !o.express_max_price) return false;
      if (catFilter !== "all" && o.category !== catFilter) return false;
      if (minRating > 0 && (o.restaurant?.rating_avg || 0) < minRating && (o.restaurant?.rating_count || 0) > 0) return false;
      if (minPayScore > 0) {
        const ps = o.restaurant?.payment_score;
        // exclude only if we have a real score (stars>0) that fails the threshold.
        // "Nuovo" (stars=0) is NOT excluded so new restaurants stay discoverable.
        if (ps && ps.stars > 0 && ps.stars < minPayScore) return false;
      }
      if (maxPrice && o.lowest_price != null && o.lowest_price > Number(maxPrice)) return false;
      return true;
    });
  }, [open, catFilter, minRating, minPayScore, maxPrice, expressOnly]);

  const stats = [
    { l: "Aste disponibili", v: open.length, icon: Gavel, tab: "open" },
    { l: "Ordini vinti attivi", v: activeWon.length, icon: Trophy, tab: "won" },
    { l: "Consegnati", v: delivered.length, icon: Package, tab: "delivered" },
    { l: "Fedeltà", v: `${user?.loyalty?.stars || 0}★`, icon: Award, tab: "loyalty" },
    { l: "Rating medio", v: (user?.rating_avg || 0).toFixed(1), icon: Star, tab: "ratings" },
  ];

  const payoutChanged = pp.trim().toLowerCase() !== (user?.paypal_email || "") || iban.replace(/\s/g, "") !== (user?.iban || "") || ibanHolder.trim() !== (user?.iban_holder || "");
  const missingPaypal = !user?.iban;
  const missingAddress = !user?.address || user?.lat == null;

  const saveProfile = async () => {
    setProfSaving(true);
    try {
      await api.patch("/auth/me", {
        paypal_email: pp,
        iban: iban,
        iban_holder: ibanHolder,
        current_password: payoutPassword || undefined,
        address: address,
        working_days: workDays,
        working_hours_from: workFrom || "",
        working_hours_to: workTo || "",
        closed_dates: closedDates,
        delivery_radius_km: Number(radius),
        vacation_mode: vacMode,
        vacation_until: vacUntil || null,
      });
      toast.success("Profilo aggiornato");
      setPayoutPassword("");
      await refresh();
      setProfileOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setProfSaving(false); }
  };

  const togglePush = async () => {
    if (push.subscribed) {
      const r = await push.unsubscribe();
      if (r.ok) toast.success("Notifiche push disattivate");
      else toast.error(r.error);
    } else {
      const r = await push.subscribe();
      if (r.ok) toast.success("Notifiche push attivate!");
      else toast.error(r.error);
    }
  };

  const downloadReport = async () => {
    setRepBusy(true);
    try {
      const res = await fetch(`${API}/reports/supplier/monthly?year=${repYear}&month=${repMonth}`, {
        method: "GET",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Errore generazione report");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `astaalimentare-report-${repYear}-${String(repMonth).padStart(2,"0")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Report scaricato");
      setRepOpen(false);
    } catch (e) { toast.error(e.message); }
    finally { setRepBusy(false); }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Dashboard Fornitore</div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Ciao, {user?.business_name || user?.name}</h1>
          <p className="mt-1 text-sm text-slate-600">Vedi gli ordini nelle tue categorie e fai le tue offerte.</p>
          <div className="mt-2"><LoyaltyInfoDialog role="supplier" /></div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Button onClick={() => { setTab("offers"); setTimeout(() => document.getElementById("supplier-tabs")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50); }} className="ga-btn-primary rounded-full" data-testid="sell-goods-button"><Tag className="w-4 h-4 mr-2" />Vendi la tua merce</Button>
          <Button
            variant="outline"
            onClick={toggleSound}
            className="rounded-full"
            data-testid="sound-toggle-button"
            title={soundEnabled ? "Silenzia suono ordini express" : "Riattiva suono ordini express"}
          >
            {soundEnabled ? <Volume2 className="w-4 h-4 mr-2" /> : <VolumeX className="w-4 h-4 mr-2" />}
            {soundEnabled ? "Suono attivo" : "Suono muto"}
          </Button>
          {push.supported && push.enabled && (
            <Button variant="outline" onClick={togglePush} disabled={push.busy} className="rounded-full" data-testid="push-toggle-button">
              {push.subscribed ? <BellOff className="w-4 h-4 mr-2" /> : <Bell className="w-4 h-4 mr-2" />}
              {push.subscribed ? "Push attive" : "Attiva push"}
            </Button>
          )}
          <Dialog open={repOpen} onOpenChange={setRepOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-full" data-testid="report-button">
                <FileText className="w-4 h-4 mr-2" />Report mensile
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Scarica report mensile</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-slate-600">Genera un PDF con tutti i tuoi ordini vinti e il fatturato del mese.</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Mese</Label>
                    <Select value={String(repMonth)} onValueChange={v => setRepMonth(Number(v))}>
                      <SelectTrigger data-testid="report-month-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"].map((m,i)=>(
                          <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Anno</Label>
                    <Input type="number" value={repYear} onChange={e => setRepYear(Number(e.target.value))} min={2024} max={2030} data-testid="report-year-input" />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={downloadReport} disabled={repBusy} className="ga-btn-primary rounded-full" data-testid="download-report-button">
                  <Download className="w-4 h-4 mr-2" />{repBusy ? "Generazione..." : "Scarica PDF"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
            <DialogTrigger asChild>
              <Button variant={missingPaypal ? "default" : "outline"} className={`rounded-full ${missingPaypal ? "ga-btn-primary" : ""}`} data-testid="edit-profile-button">
                {missingPaypal ? "Aggiungi IBAN" : "Modifica profilo"}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Modifica profilo</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="pp">Email PayPal per accrediti</Label>
                  <Input id="pp" type="email" value={pp} onChange={e => setPp(e.target.value)} placeholder="pagamenti@tuoazienda.it" data-testid="paypal-email-input" />
                  <div className="mt-1 text-xs text-slate-500">Usata per gli accrediti automatici degli ordini pagati con PayPal.</div>
                </div>
                <div>
                  <Label htmlFor="iban">IBAN per bonifici e accrediti carta</Label>
                  <Input id="iban" value={iban} onChange={e => setIban(e.target.value.toUpperCase())} placeholder="IT60X0542811101000000123456" className="font-mono" data-testid="iban-input" />
                  <div className="mt-1 text-xs text-slate-500">Mostrato all'acquirente per il bonifico istantaneo e usato per accreditarti i pagamenti con carta.</div>
                </div>
                <div>
                  <Label htmlFor="ibanh">Intestatario conto</Label>
                  <Input id="ibanh" value={ibanHolder} onChange={e => setIbanHolder(e.target.value)} placeholder={user?.business_name || "Ragione sociale"} data-testid="iban-holder-input" />
                </div>
                {payoutChanged && (
                  <div>
                    <Label htmlFor="payout-pw">Password attuale</Label>
                    <Input id="payout-pw" type="password" autoComplete="current-password" value={payoutPassword} onChange={e => setPayoutPassword(e.target.value)} data-testid="payout-password-input" />
                    <p className="mt-1 text-xs text-slate-500">Serve per cambiare PayPal, IBAN o intestatario. Ti invieremo un'email di conferma.</p>
                  </div>
                )}
                <div>
                  <Label htmlFor="addr">Indirizzo sede / magazzino</Label>
                  <Input id="addr" value={address} onChange={e => setAddress(e.target.value)} placeholder="Via Roma 10, 10121 Torino" data-testid="supplier-address-input" />
                  <div className="mt-1 text-xs text-slate-500">Serve a calcolare i km dalle zone di consegna e a ricevere gli ordini in zona.</div>
                </div>
                <div>
                  <Label htmlFor="rad">Raggio consegna (km)</Label>
                  <Input id="rad" type="number" min={0} step="1" value={radius} onChange={e => setRadius(Number(e.target.value))} placeholder="0 = illimitato" data-testid="radius-input" />
                  <div className="mt-1 text-xs text-slate-500">Vedrai solo aste con indirizzo di consegna entro questo raggio.</div>
                </div>
                <div className="border-t border-slate-100 pt-4">
                  <Label>Giorni e orari lavorativi</Label>
                  <div className="mt-2 flex gap-1.5 flex-wrap" data-testid="working-days">
                    {["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"].map((d, i) => {
                      const on = workDays.includes(i);
                      return (
                        <button key={d} type="button" onClick={() => setWorkDays(on ? workDays.filter(x => x !== i) : [...workDays, i].sort())}
                          className={`rounded-full px-3 py-1 text-xs font-mono font-semibold border transition-colors ${on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-300 hover:border-slate-400"}`}
                          data-testid={`working-day-${i}`} aria-pressed={on}>{d}</button>
                      );
                    })}
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="wf" className="text-xs">Dalle (opzionale)</Label>
                      <Input id="wf" type="time" value={workFrom} onChange={e => setWorkFrom(e.target.value)} data-testid="working-hours-from" />
                    </div>
                    <div>
                      <Label htmlFor="wt" className="text-xs">Alle</Label>
                      <Input id="wt" type="time" value={workTo} onChange={e => setWorkTo(e.target.value)} data-testid="working-hours-to" />
                    </div>
                  </div>
                  <div className="mt-1 text-xs text-slate-500">Gli ordini con consegna richiesta nei giorni/orari in cui sei chiuso non ti verranno mostrati né notificati.</div>
                  <div className="mt-4">
                    <Label className="text-xs">Chiusure straordinarie (festività, ferie brevi)</Label>
                    <div className="mt-1.5 flex gap-2">
                      <Input type="date" value={newClosed} min={new Date().toISOString().slice(0, 10)} onChange={e => setNewClosed(e.target.value)} data-testid="closed-date-input" />
                      <Button type="button" variant="outline" className="rounded-full shrink-0" disabled={!newClosed || closedDates.includes(newClosed)}
                        onClick={() => { setClosedDates([...closedDates, newClosed].sort()); setNewClosed(""); }} data-testid="closed-date-add">Aggiungi</Button>
                    </div>
                    {closedDates.length > 0 && (
                      <div className="mt-2 flex gap-1.5 flex-wrap" data-testid="closed-dates-list">
                        {closedDates.map(d => (
                          <span key={d} className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-300 text-amber-900 px-2.5 py-1 text-xs font-mono" data-testid={`closed-date-${d}`}>
                            {d.split("-").reverse().join("/")}
                            <button type="button" onClick={() => setClosedDates(closedDates.filter(x => x !== d))} className="ml-0.5 text-amber-700 hover:text-amber-950" aria-label="Rimuovi" data-testid={`closed-date-remove-${d}`}>×</button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="border-t border-slate-100 pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 font-semibold text-slate-900">
                        <Palmtree className="w-4 h-4 text-amber-500" /> Modalità vacanza
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">Sospendi la ricezione di nuove aste.</div>
                    </div>
                    <Switch checked={vacMode} onCheckedChange={setVacMode} data-testid="vacation-switch" />
                  </div>
                  {vacMode && (
                    <div className="mt-3">
                      <Label htmlFor="vac_until">Torno il (opzionale)</Label>
                      <Input id="vac_until" type="date" value={vacUntil} onChange={e => setVacUntil(e.target.value)} data-testid="vacation-until-input" />
                      <div className="mt-1 text-xs text-slate-500">Se lasci vuoto, la vacanza rimane attiva finché non la disattivi.</div>
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button onClick={saveProfile} disabled={profSaving} className="ga-btn-primary rounded-full" data-testid="save-profile-button">
                  <Save className="w-4 h-4 mr-2" />{profSaving ? "Salvataggio..." : "Salva"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <CommissionDuesBanner onChanged={refresh} />
      {missingAddress && (
        <div className="mt-4 flex items-start gap-3 rounded-2xl border border-sky-200 bg-sky-50 p-4" data-testid="missing-address-banner">
          <MapPin className="w-5 h-5 text-sky-700 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-sky-900">{user?.address ? "Indirizzo sede non riconosciuto" : "Inserisci l'indirizzo della tua sede"}</div>
            <div className="text-xs text-sky-800 mt-0.5">
              {user?.address ? "Non siamo riusciti a localizzarlo: controlla via, CAP e città." : "Con l'indirizzo vedi i km stimati accanto a ogni asta, puoi filtrare per raggio di consegna e ricevi solo gli ordini in zona."}
            </div>
          </div>
          <Button size="sm" className="ga-btn-primary rounded-full shrink-0" onClick={() => setProfileOpen(true)} data-testid="open-missing-address">
            Aggiungi indirizzo
          </Button>
        </div>
      )}
      {user?.activity?.suggest_vacation && !user?.vacation_mode && (
        <div className="mt-4 flex items-start gap-3 rounded-2xl border border-indigo-200 bg-indigo-50 p-4" data-testid="vacation-suggestion-banner">
          <Palmtree className="w-5 h-5 text-indigo-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-indigo-900">Attivare la modalità vacanza?</div>
            <div className="text-xs text-indigo-800 mt-0.5">
              Sono passate <span className="font-mono font-bold">{user.activity.closed_recent}</span> aste nelle tue categorie negli ultimi 14 giorni senza tue offerte. Sospendi la ricezione così eviti notifiche inutili.
            </div>
          </div>
          <Button size="sm" className="ga-btn-primary rounded-full shrink-0" onClick={() => setProfileOpen(true)} data-testid="open-vacation-suggestion">
            Attiva vacanza
          </Button>
        </div>
      )}

      {user?.vacation_mode && (
        <div className="mt-4 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="vacation-banner">
          <Palmtree className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900 flex-1">
            <span className="font-semibold">Modalità vacanza attiva</span>
            {user.vacation_until && <> · torni il {new Date(user.vacation_until).toLocaleDateString("it-IT")}</>}
            . Non riceverai nuove aste finché non la disattivi.
          </div>
        </div>
      )}

      <div className="mt-4 flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4" data-testid="escrow-explainer-banner">
        <ShieldCheck className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
        <div className="text-sm text-emerald-900">
          <span className="font-semibold">Pagamento protetto, rilascio rapido.</span> Quando segni un ordine come consegnato, l'acquirente ha <b>1 ora</b> (ordini Express e Offerte) o <b>6 ore</b> (ordini in asta) per confermare o contestare; il conteggio parte all'interno della fascia oraria di consegna che ha indicato. Se conferma ricevi subito il payout; se non fa nulla, allo scadere del termine la consegna viene confermata automaticamente e il pagamento ti viene rilasciato.
        </div>
      </div>

      {missingPaypal && (
        <div className="mt-4 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="iban-missing-banner">
          <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <span className="font-semibold">Aggiungi il tuo IBAN</span> per ricevere i pagamenti: serve agli acquirenti per il bonifico istantaneo e per accreditarti gli ordini pagati con carta.
          </div>
        </div>
      )}

      <PendingRatingsBanner role="supplier" testId="sup-pending-ratings-banner" />

      <ConformityClauseGate />
      <div className="mt-6"><WhatsAppPrefs role="supplier" /></div>
      <div className="mt-4"><ChangePasswordCard /></div>

      <div className={`mt-4 ga-card p-5 ${isPremium ? "border-emerald-300 bg-emerald-50/40" : inOnboarding ? "border-indigo-200 bg-indigo-50/40" : "border-slate-200"}`} data-testid="supplier-premium-card">
        {isPremium ? (
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="text-xs font-mono uppercase tracking-widest text-emerald-700 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" /> Premium Fornitore attivo · {curPlanLabel}
              </div>
              <div className="mt-1 font-display text-xl font-bold text-slate-900">Commissione ridotta + boost visibilità</div>
              <div className="text-xs text-slate-600 mt-1">
                Trattieni fino al 99% di ogni transazione. Le tue offerte hanno la spunta Premium visibile agli acquirenti.
              </div>
            </div>
            <Button onClick={openPortal} disabled={portalBusy} variant="outline" className="rounded-full shrink-0" data-testid="manage-supplier-subscription-button">
              {portalBusy ? "..." : "Gestisci abbonamento"}
            </Button>
          </div>
        ) : inOnboarding ? (
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="text-xs font-mono uppercase tracking-widest text-indigo-700 flex items-center gap-1.5">
                <Rocket className="w-3.5 h-3.5" /> Onboarding · {daysLeft} giorni rimanenti
              </div>
              <div className="mt-1 font-display text-xl font-bold text-slate-900">Sei nei tuoi primi 45 giorni: solo 2% di commissione</div>
              <div className="text-xs text-slate-600 mt-1">
                Approfittane per fare offerte aggressive e vincere i primi clienti con la commissione più bassa del listino.
              </div>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="text-xs font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" /> Piano Free · Commissione 6% (min €1,50/ordine)
                </div>
                <div className="mt-1 font-display text-xl font-bold text-slate-900">Riduci la commissione con un piano Premium</div>
                <div className="text-xs text-slate-600 mt-1">
                  Scegli il piano che ti serve: paghi meno su ogni transazione e ottieni badge Premium visibile nelle aste.
                </div>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="supplier-plans-grid">
              <PlanTile
                testId="upgrade-basic-button"
                name="Basic"
                price="€ 29,99"
                fee="4% commissione"
                bullets={["Badge Premium visibile", "Priorità supporto standard"]}
                onClick={() => upgrade("basic")}
                disabled={upgrading}
                tone="basic"
              />
              <PlanTile
                testId="upgrade-pro-button"
                name="Pro"
                badge="Consigliato"
                price="€ 99,99"
                fee="2,5% commissione"
                bullets={["Boost visibilità nelle aste", "Badge Premium prioritario", "Supporto rapido"]}
                onClick={() => upgrade("pro")}
                disabled={upgrading}
                tone="pro"
              />
              <PlanTile
                testId="upgrade-enterprise-button"
                name="Enterprise"
                price="€ 299"
                fee="1% commissione"
                bullets={["Solo 1% su ogni ordine", "Account manager dedicato", "SLA prioritario"]}
                onClick={() => upgrade("enterprise")}
                disabled={upgrading}
                tone="enterprise"
              />
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((s) => (
          <button type="button" key={s.l} onClick={() => selectTab(s.tab)}
            className={`ga-card p-5 text-left transition-all hover:-translate-y-0.5 hover:shadow-md hover:border-slate-400 cursor-pointer ${tab === s.tab ? "ring-2 ring-emerald-500 border-emerald-500" : ""}`}
            data-testid={`sup-stat-${s.l.toLowerCase().replace(/\s+/g,'-')}`}>
            <div className="flex items-center justify-between">
              <div className="text-xs font-mono uppercase tracking-widest text-slate-500">{s.l}</div>
              <s.icon className="w-4 h-4 text-slate-400" />
            </div>
            <div className="mt-2 font-display text-3xl font-extrabold text-slate-900">{s.v}</div>
            <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-emerald-700">Vedi dettagli →</div>
          </button>
        ))}
      </div>

      <div ref={tabsRef} />
      <Tabs value={tab} onValueChange={setTab} className="mt-10" id="supplier-tabs">
        <TabsList className="bg-white border border-slate-200 flex-wrap">
          <TabsTrigger value="open" data-testid="sup-tab-open">Aste aperte ({filteredOpen.length})</TabsTrigger>
          <TabsTrigger value="won" data-testid="sup-tab-won">Vinti attivi ({activeWon.length})</TabsTrigger>
          <TabsTrigger value="delivered" data-testid="sup-tab-delivered">Consegnati ({delivered.length})</TabsTrigger>
          <TabsTrigger value="offers" data-testid="sup-tab-offers">Vendi la tua merce</TabsTrigger>
          <TabsTrigger value="loyalty" data-testid="sup-tab-loyalty">Fedeltà</TabsTrigger>
          <TabsTrigger value="ratings" data-testid="sup-tab-ratings">Recensioni ({user?.rating_count || 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="open" className="mt-6">
          <div className="ga-card p-4 sm:p-5 mb-5" data-testid="filters-panel">
            <div className="flex items-center gap-2 text-slate-800">
              <Filter className="w-4 h-4" />
              <span className="font-semibold text-sm">Filtri</span>
              <button
                onClick={() => setExpressOnly(v => !v)}
                className={`ml-auto inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-mono uppercase tracking-wider transition ${expressOnly ? "bg-emerald-600 text-white border-emerald-700 shadow-sm" : "bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50"}`}
                data-testid="filter-express-toggle"
              >
                ⚡ Solo Express
              </button>
            </div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">Categoria</Label>
                <Select value={catFilter} onValueChange={setCatFilter}>
                  <SelectTrigger data-testid="filter-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte</SelectItem>
                    {(user?.categories || []).map(cid => {
                      const c = cats.find(x => x.id === cid);
                      return <SelectItem key={cid} value={cid} data-testid={`filter-cat-${cid}`}>{c?.label || cid}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Rating minimo acquirente</Label>
                <Select value={String(minRating)} onValueChange={v => setMinRating(Number(v))}>
                  <SelectTrigger data-testid="filter-rating-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Qualsiasi</SelectItem>
                    <SelectItem value="3">★ 3+</SelectItem>
                    <SelectItem value="4">★ 4+</SelectItem>
                    <SelectItem value="4.5">★ 4.5+</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Affidabilità pagamenti</Label>
                <Select value={String(minPayScore)} onValueChange={v => setMinPayScore(Number(v))}>
                  <SelectTrigger data-testid="filter-payscore-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0" data-testid="filter-payscore-0">Qualsiasi</SelectItem>
                    <SelectItem value="3" data-testid="filter-payscore-3">Almeno 3★ Buono</SelectItem>
                    <SelectItem value="4" data-testid="filter-payscore-4">Almeno 4★ Ottimo</SelectItem>
                    <SelectItem value="5" data-testid="filter-payscore-5">Solo 5★ Eccellente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Prezzo massimo attuale (€)</Label>
                <Input type="number" min={0} step="0.5" placeholder="es. 200" value={maxPrice} onChange={e => setMaxPrice(e.target.value)} data-testid="filter-max-price-input" />
              </div>
            </div>
            {(catFilter !== "all" || minRating > 0 || minPayScore > 0 || maxPrice || expressOnly) && (
              <div className="mt-3">
                <button className="text-xs font-mono uppercase tracking-widest text-slate-500 hover:text-slate-900" onClick={() => { setCatFilter("all"); setMinRating(0); setMinPayScore(0); setMaxPrice(""); setExpressOnly(false); }} data-testid="reset-filters-button">
                  Azzera filtri
                </button>
              </div>
            )}
          </div>

          <OrderList orders={filteredOpen} empty={open.length ? "Nessuna asta corrisponde ai filtri." : "Nessuna asta aperta nelle tue categorie al momento."} loading={loading} showCountdown />
        </TabsContent>
        <TabsContent value="won" className="mt-6">
          <OrderList orders={activeWon} empty="Nessun ordine vinto in corso." loading={loading} />
        </TabsContent>
        <TabsContent value="delivered" className="mt-6">
          <OrderList orders={delivered} empty="Nessun ordine consegnato finora." loading={loading} />
        </TabsContent>
        <TabsContent value="loyalty" className="mt-6">
          <LoyaltyPanel loyalty={user?.loyalty} />
        </TabsContent>
        <TabsContent value="offers" className="mt-6">
          <SupplierOffersPanel categories={user?.categories || []} cats={cats} />
        </TabsContent>
        <TabsContent value="ratings" className="mt-6">
          <ReceivedRatings avg={user?.rating_avg} count={user?.rating_count} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function PlanTile({ testId, name, badge, price, fee, bullets, onClick, disabled, tone }) {
  const toneMap = {
    basic: {
      border: "border-emerald-200",
      accent: "text-emerald-700",
      button: "border border-emerald-300 text-emerald-800 hover:bg-emerald-50",
    },
    pro: {
      border: "border-amber-300 ring-2 ring-amber-200",
      accent: "text-amber-700",
      button: "ga-btn-primary text-white",
    },
    enterprise: {
      border: "border-slate-900",
      accent: "text-slate-900",
      button: "border border-slate-900 text-slate-900 hover:bg-slate-100",
    },
  };
  const t = toneMap[tone] || toneMap.basic;
  return (
    <div className={`relative rounded-2xl border ${t.border} bg-white p-4 flex flex-col`} data-testid={`plan-tile-${tone}`}>
      {badge && (
        <span className="absolute -top-2 right-3 inline-flex items-center rounded-full bg-amber-500 text-white px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest shadow-sm">
          {badge}
        </span>
      )}
      <div className={`text-xs font-mono uppercase tracking-widest ${t.accent}`}>{name}</div>
      <div className="mt-1 font-display text-2xl font-extrabold text-slate-900">{price}<span className="text-sm font-normal text-slate-500">/mese</span></div>
      <div className={`text-xs font-mono ${t.accent} mt-0.5`}>{fee}</div>
      <ul className="mt-3 space-y-1 text-xs text-slate-600 flex-1">
        {bullets.map(b => (
          <li key={b} className="flex gap-1.5">
            <span className="text-slate-400">·</span><span>{b}</span>
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={`mt-4 w-full rounded-full py-2 text-xs font-semibold uppercase tracking-widest transition ${t.button} disabled:opacity-50`}
        data-testid={testId}
      >
        Attiva {name}
      </button>
    </div>
  );
}

const LOYALTY_STEPS = [[1, 1], [2, 5], [3, 15], [4, 40], [5, 100]];
function LoyaltyPanel({ loyalty }) {
  const n = loyalty?.delivered || 0;
  const stars = loyalty?.stars || 0;
  const next = LOYALTY_STEPS.find(([s]) => s > stars);
  return (
    <div className="ga-card p-5" data-testid="loyalty-panel">
      <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Livello fedeltà</div>
      <div className="mt-1 flex items-center gap-1">
        {[1,2,3,4,5].map(k => <Star key={k} className={`w-7 h-7 ${k <= stars ? "fill-amber-500 text-amber-500" : "text-slate-300"}`} />)}
      </div>
      <div className="mt-2 text-sm text-slate-700"><b>{n}</b> ordini consegnati</div>
      {next ? (
        <div className="mt-3">
          <div className="text-xs text-slate-600">Prossimo livello ({next[0]}★) a {next[1]} consegne · ne mancano <b>{Math.max(0, next[1] - n)}</b></div>
          <div className="mt-1 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-amber-500" style={{ width: `${Math.min(100, (n / next[1]) * 100)}%` }} /></div>
        </div>
      ) : <div className="mt-3 text-xs text-emerald-700 font-semibold">Livello massimo raggiunto!</div>}
      <div className="mt-4 grid grid-cols-5 gap-2 text-center text-[11px] text-slate-600">
        {LOYALTY_STEPS.map(([s, req]) => (
          <div key={s} className={`rounded-lg border p-2 ${s <= stars ? "border-amber-300 bg-amber-50" : "border-slate-200"}`}><div className="font-bold">{s}★</div><div>{req} consegne</div></div>
        ))}
      </div>
      <div className="mt-3"><LoyaltyInfoDialog role="supplier" /></div>
    </div>
  );
}

function OrderList({ orders, empty, loading, showCountdown }) {
  if (loading) return <div className="text-sm text-slate-500 py-6">Caricamento...</div>;
  if (!orders.length) return <div className="ga-card p-8 text-center text-slate-500 text-sm">{empty}</div>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
      {orders.map(o => (
        <Link key={o.id} to={`/ordine/${o.id}`} className="ga-card p-5 block" data-testid={`sup-order-${o.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-display text-lg font-bold text-slate-900 truncate">{o.title}</div>
                {o.express_max_price && (
                  <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 px-2 py-0.5 text-[10px] font-mono font-bold uppercase tracking-wider ga-tick" data-testid={`sup-express-badge-${o.id}`}>
                    ⚡ Express · € {o.express_max_price.toFixed(2)} · prima offerta vince
                  </span>
                )}
              </div>
              <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mt-1">{o.category}{o.delivery_date ? ` · ${o.delivery_date}` : ""}{o.delivery_time_from && o.delivery_time_to ? ` · ${o.delivery_time_from}–${o.delivery_time_to}` : ""}</div>
            </div>
            <Badge className={`shrink-0 ${STATUS_COLOR[o.status] || ""}`}>{STATUS_LABEL[o.status] || o.status}</Badge>
          </div>
          <div className="mt-3 text-sm text-slate-600">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium">{o.restaurant?.name}</span>
              {o.restaurant?.rating_count > 0 && (
                <span className="text-xs text-slate-500">★ {o.restaurant.rating_avg}</span>
              )}
              {o.distance_km != null && (
                <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 text-sky-800 border border-sky-200 px-2 py-0.5 text-[11px] font-mono font-semibold" title="Distanza in linea d'aria dalla tua sede" data-testid={`sup-order-distance-${o.id}`}>
                  <MapPin className="w-3 h-3" /> ~{o.distance_km} km
                </span>
              )}
            </div>
            {o.restaurant?.payment_score && (
              <div className="mt-1.5">
                <PaymentScoreBadge score={o.restaurant.payment_score} size="sm" testId={`sup-order-payscore-${o.id}`} />
              </div>
            )}
          </div>
          <div className="mt-3 flex items-center justify-between">
            <div className="text-xs text-slate-500">{o.bids_count} offerte</div>
            {o.lowest_price != null ? (
              <div className="font-mono text-sm font-bold text-emerald-700">min € {o.lowest_price.toFixed(2)}</div>
            ) : (o.auction_visibility === "sealed" && o.bids_count > 0 && <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400" data-testid={`sealed-badge-${o.id}`}>busta chiusa</div>)}
          </div>
          {showCountdown && o.status === "open" && (o.choice_ends_at ? (
            <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500" data-testid={`sup-choice-wait-${o.id}`}>Asta chiusa: in attesa della scelta del ristorante</div>
          ) : (
            <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
              <div className="text-xs text-slate-500">Chiude tra</div>
              <Countdown endsAt={o.auction_ends_at} testId={`sup-countdown-${o.id}`} />
            </div>
          ))}
        </Link>
      ))}
    </div>
  );
}
